import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../App';
import { 
  ArrowLeft, 
  Clock, 
  User, 
  UserPlus, 
  UserMinus, 
  Bell, 
  XCircle,
  CheckCircle,
  Calendar,
  RefreshCw,
  Home
} from 'lucide-react';
import { useSafeBack } from '../hooks/useSafeBack';
import { parseUTC } from '../utils/datetime';

// Host-friendly event labels (no internal details)
const EVENT_LABELS = {
  GAME_CREATED: 'Game created',
  GAME_CANCELED: 'Game canceled',
  INVITE_SENT: 'Invitation sent',
  INVITE_ACCEPTED: 'Invitation accepted',
  USER_JOINED: 'Player joined',
  USER_LEFT: 'Player left',
  WAITLIST_PROMOTED: 'Player promoted from waitlist',
  PLAYER_REMOVED: 'Player removed',
  CONFIRMATION_REQUEST_SENT: 'Confirmation request sent',
  USER_CONFIRMED: 'Player confirmed',
  NOTIFICATION_SENT: 'Notification delivered',
};

const EVENT_ICONS = {
  GAME_CREATED: Calendar,
  GAME_CANCELED: XCircle,
  INVITE_SENT: Bell,
  INVITE_ACCEPTED: CheckCircle,
  USER_JOINED: UserPlus,
  USER_LEFT: UserMinus,
  WAITLIST_PROMOTED: UserPlus,
  PLAYER_REMOVED: UserMinus,
  CONFIRMATION_REQUEST_SENT: Bell,
  USER_CONFIRMED: CheckCircle,
  NOTIFICATION_SENT: Bell,
};

const EVENT_COLORS = {
  GAME_CREATED: 'text-green-600',
  GAME_CANCELED: 'text-red-600',
  INVITE_SENT: 'text-blue-500',
  INVITE_ACCEPTED: 'text-green-500',
  USER_JOINED: 'text-green-600',
  USER_LEFT: 'text-yellow-600',
  WAITLIST_PROMOTED: 'text-purple-600',
  PLAYER_REMOVED: 'text-red-500',
  CONFIRMATION_REQUEST_SENT: 'text-blue-600',
  USER_CONFIRMED: 'text-green-600',
  NOTIFICATION_SENT: 'text-green-500',
};

export default function GameHistory() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { goBack } = useSafeBack('/dashboard');
  const [events, setEvents] = useState([]);
  const [gameInfo, setGameInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await apiClient.get(`/games/${gameId}/history`);
        
        setEvents(response.data.events || []);
        setGameInfo({
          title: response.data.game_title,
          event_count: response.data.event_count
        });
      } catch (err) {
        if (err.response?.status === 403) {
          setError('Only the game host can view this history');
        } else {
          setError(err.response?.data?.detail || err.message || 'Failed to fetch history');
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (gameId) {
      fetchHistory();
    }
  }, [gameId]);
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = parseUTC(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  const getEventDescription = (event) => {
    const label = EVENT_LABELS[event.event_type] || event.event_type;
    
    // Add context based on event type
    if (event.target_username) {
      if (event.event_type === 'INVITE_SENT') {
        return `Invited ${event.target_username}`;
      }
      if (event.event_type === 'INVITE_ACCEPTED') {
        return `${event.target_username} accepted invite`;
      }
      if (event.event_type === 'USER_JOINED') {
        return `${event.target_username} joined`;
      }
      if (event.event_type === 'USER_LEFT') {
        return `${event.target_username} left`;
      }
      if (event.event_type === 'PLAYER_REMOVED') {
        return `${event.target_username} was removed`;
      }
      if (event.event_type === 'WAITLIST_PROMOTED') {
        return `${event.target_username} promoted from waitlist`;
      }
      if (event.event_type === 'USER_CONFIRMED') {
        return `${event.target_username} confirmed`;
      }
    }
    
    return label;
  };
  
  return (
    <div className="min-h-screen bg-gray-50 p-4" data-testid="game-history-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="nav-home-btn"
              aria-label="Dashboard"
            >
              <Home className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          
          <h1 className="text-xl font-bold text-gray-900">Game History</h1>
          {gameInfo && (
            <p className="text-gray-500 text-sm mt-1">{gameInfo.title}</p>
          )}
        </div>
        
        {/* Timeline */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg text-sm">
            {error}
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500 text-sm">
            No history recorded yet.
          </div>
        ) : (
          <div className="space-y-3">
            {events.map((event, index) => {
              const IconComponent = EVENT_ICONS[event.event_type] || Bell;
              const color = EVENT_COLORS[event.event_type] || 'text-gray-500';
              
              return (
                <div
                  key={event.id || index}
                  className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3"
                  data-testid={`history-event-${index}`}
                >
                  <div className={`${color}`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 truncate">
                      {getEventDescription(event)}
                    </p>
                    {event.roster_count_snapshot !== null && event.roster_count_snapshot !== undefined && (
                      <p className="text-xs text-gray-400">
                        {event.roster_count_snapshot} player{event.roster_count_snapshot !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-400 whitespace-nowrap">
                    {formatTime(event.created_at)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
